import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Key, Copy, Check, RefreshCw, Eye, EyeOff, AlertTriangle, Shield, User,
  Code, Terminal, ChevronDown, ChevronUp, Activity, Coins, Clock,
  BarChart2, Zap, Info, ExternalLink, FileCode, Play, Plug, FileText,
  Loader2
} from 'lucide-react';
import { apiKeysAPI } from '../../api';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';

// ==================== Reusable Components ====================

// Code Block Component with Copy
const CodeBlock = ({ title, code, language = 'bash' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-slate-100 border-b border-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          <Terminal className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-medium text-slate-700 truncate">{title}</span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 sm:gap-1.5 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
          data-testid="copy-code-btn"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <div className="overflow-x-auto">
        <pre className="p-3 sm:p-4 text-[10px] sm:text-sm bg-slate-900 text-slate-100 whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
};

// Response Preview Component
const ResponsePreview = ({ title, response }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 sm:px-4 py-2 bg-green-50 hover:bg-green-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-green-600" />
          <span className="text-xs sm:text-sm font-medium text-green-700">{title}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-green-600" /> : <ChevronDown className="w-4 h-4 text-green-600" />}
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="overflow-x-auto">
              <pre className="p-3 sm:p-4 text-[10px] sm:text-xs bg-slate-50 text-slate-700 border-t border-slate-200 whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal">
                <code>{JSON.stringify(response, null, 2)}</code>
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, title, value, subtitle, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-3 sm:p-4 ${className}`}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500 truncate">{title}</p>
        <p className="text-lg sm:text-xl font-bold text-slate-900">{value}</p>
        {subtitle && <p className="text-[10px] sm:text-xs text-slate-400 truncate">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// ==================== Main Component ====================

const Integrations = () => {
  const [apiKeyData, setApiKeyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copiedPartnerId, setCopiedPartnerId] = useState(false);
  
  // Hero tabs: 'api-keys' | 'api-docs'
  const [heroTab, setHeroTab] = useState('api-keys');
  // API Docs sub-tabs: 'tryon' | 'moments' | 'size'
  const [docsTab, setDocsTab] = useState('tryon');
  // Operations tabs: 'usage' | 'logs'
  const [opsTab, setOpsTab] = useState('usage');

  // Get user info from localStorage
  const userStr = localStorage.getItem('nanokart_user');
  const user = userStr ? JSON.parse(userStr) : {};
  const partnerShortId = user?.partner_short_id || '';
  const partnerId = user?.partner_id || '';
  const displayId = partnerShortId || partnerId;

  useEffect(() => {
    fetchKeyData();
  }, []);

  const fetchKeyData = async () => {
    try {
      const response = await apiKeysAPI.get();
      setApiKeyData(response.data);
    } catch (error) {
      toast.error('Failed to load API key data');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await apiKeysAPI.regenerate();
      setNewKey(response.data.api_key);
      toast.success('API key regenerated successfully!');
      fetchKeyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate key');
    } finally {
      setRegenerating(false);
      setShowConfirm(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
    toast.success('Copied to clipboard!');
  };

  const copyPartnerId = () => {
    if (displayId) {
      navigator.clipboard.writeText(displayId);
      setCopiedPartnerId(true);
      setTimeout(() => setCopiedPartnerId(false), 2000);
      toast.success('Partner ID copied!');
    }
  };

  const maskApiKey = (key) => {
    if (!key) return '••••••••••••••••••••••••••••••••';
    return key.substring(0, 8) + '••••••••••••••••••••' + key.substring(key.length - 4);
  };

  const formatCredits = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num?.toString() || '0';
  };

  // API Examples
  const baseUrl = 'https://api.nanokart.ai/api/v1';
  const apiKey = apiKeyData?.api_key || 'YOUR_API_KEY';
  
  const tryonExample = `curl -X POST "${baseUrl}/tryon/external" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "human_image_url": "https://example.com/model.jpg",
    "garment_image_url": "https://example.com/garment.jpg",
    "sku_id": "SKU001",
    "customer_id": "customer_123"
  }'`;

  const tryonResponse = {
    "request_id": "550e8400-e29b-41d4-a716-446655440000",
    "status": "success",
    "tryon_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "image_url": "https://cdn.nanokart.ai/results/tryon_abc123.jpg",
    "cached": false,
    "credits_consumed": 1,
    "latency_ms": 2340
  };

  const momentsExample = `curl -X POST "${baseUrl}/moments/external" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tryon_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "moment_type": "creative_workday",
    "custom_prompt": "Professional office setting",
    "sku_id": "SKU001",
    "customer_id": "customer_123"
  }'`;

  const momentsResponse = {
    "request_id": "660e8400-e29b-41d4-a716-446655440001",
    "status": "success",
    "image_url": "https://cdn.nanokart.ai/results/moments_xyz789.jpg",
    "moment_type": "creative_workday",
    "credits_consumed": 2,
    "latency_ms": 3120
  };

  const momentTypes = [
    { value: 'creative_workday', label: 'Creative Workday', description: 'Professional office/work setting' },
    { value: 'unwind', label: 'Unwind', description: 'Relaxed, casual home setting' },
    { value: 'after_hours', label: 'After Hours', description: 'Evening social/party setting' },
    { value: 'escape', label: 'The Escape', description: 'Outdoor/travel/vacation setting' },
    { value: 'gentle_celebration', label: 'Gentle Celebration', description: 'Elegant celebration setting' }
  ];

  const sizeRecommendExample = `curl -X POST "${baseUrl}/size/recommend" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "image_s3_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "height_cm": 165,
    "product_id": "prod_xyz789",
    "sku_id": "SKU001",
    "customer_id": "customer_123"
  }'`;

  const sizeRecommendWithChartExample = `curl -X POST "${baseUrl}/size/recommend" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "image_s3_key": "tryon/tryon:smart:v1:abc123def456.bin",
    "height_cm": 165,
    "size_chart_cm": {
      "XS": { "chest_cm": 84, "waist_cm": 68, "hip_cm": 90 },
      "S":  { "chest_cm": 88, "waist_cm": 72, "hip_cm": 94 },
      "M":  { "chest_cm": 92, "waist_cm": 76, "hip_cm": 98 },
      "L":  { "chest_cm": 96, "waist_cm": 80, "hip_cm": 102 },
      "XL": { "chest_cm": 100, "waist_cm": 84, "hip_cm": 106 }
    }
  }'`;

  const sizeChartFetchExample = `curl -X GET "${baseUrl}/inventory/prod_xyz789/size-chart" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}"`;

  const sizeRecommendFromPhotoExample = `curl -X POST "${baseUrl}/size/recommend-from-photo" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -F "human_image=@/path/to/customer_photo.jpg" \\
  -F "height_cm=165" \\
  -F "product_id=prod_xyz789"`;

  const sizeRecommendFromPhotoWithChartExample = `curl -X POST "${baseUrl}/size/recommend-from-photo" \\
  -H "X-API-Key: ${showKey ? apiKey : 'YOUR_API_KEY'}" \\
  -H "X-Partner-Id: ${displayId}" \\
  -F "human_image=@/path/to/customer_photo.jpg" \\
  -F "height_cm=165" \\
  -F 'size_chart_cm={"XS":{"chest_cm":84},"S":{"chest_cm":88},"M":{"chest_cm":92},"L":{"chest_cm":96},"XL":{"chest_cm":100}}'`;

  const sizeRecommendFromPhotoResponse = {
    "request_id": "550e8400-e29b-41d4-a716-446655440005",
    "status": "success",
    "recommended_size": "M",
    "confidence_percent": 87,
    "fit_notes": "M fits well across chest and waist. Slight room at hips.",
    "photo_sufficient": true,
    "insufficiency_message": null,
    "engine": "v2",
    "cached": false,
    "credits_consumed": 2,
    "latency_ms": 1120
  };
  const sizeRecommendResponse = {
    "request_id": "990e8400-e29b-41d4-a716-446655440004",
    "status": "success",
    "recommended_size": "M",
    "confidence_percent": 84,
    "fit_notes": "M fits well across chest and waist. Slight room at hips.",
    "photo_sufficient": true,
    "insufficiency_message": null,
    "engine": "v2",
    "cached": false,
    "credits_consumed": 2,
    "latency_ms": 980
  };

  const sizeChartResponse = {
    "product_id": "prod_xyz789",
    "has_size_chart": true,
    "size_chart_cm": {
      "XS": { "chest_cm": 84, "waist_cm": 68, "hip_cm": 90 },
      "S":  { "chest_cm": 88, "waist_cm": 72, "hip_cm": 94 },
      "M":  { "chest_cm": 92, "waist_cm": 76, "hip_cm": 98 },
      "L":  { "chest_cm": 96, "waist_cm": 80, "hip_cm": 102 },
      "XL": { "chest_cm": 100, "waist_cm": 84, "hip_cm": 106 }
    },
    "gender": "female"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="integrations-page">
      
      {/* ==================== SECTION A: HERO ==================== */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Hero Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Plug className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Integrations</h1>
              <p className="text-slate-500 text-sm mt-1">
                Use API keys to connect Nanokart TryOn and Moments to your store.
              </p>
            </div>
          </div>
        </div>

        {/* Hero Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setHeroTab('api-keys')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              heroTab === 'api-keys'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="hero-tab-api-keys"
          >
            <Key className="w-4 h-4 inline mr-2" />
            API Keys
          </button>
          <button
            onClick={() => setHeroTab('api-docs')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              heroTab === 'api-docs'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="hero-tab-api-docs"
          >
            <Code className="w-4 h-4 inline mr-2" />
            API Docs
          </button>
        </div>

        {/* Hero Tab Content */}
        <div className="p-5 sm:p-6">
          
          {/* ========== API Keys Tab ========== */}
          {heroTab === 'api-keys' && (
            <div className="space-y-5">
              
              {/* New Key Alert */}
              <AnimatePresence>
                {newKey && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-emerald-50 border border-emerald-200 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Key className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-emerald-800 mb-1">New API Key Generated!</h3>
                        <p className="text-emerald-700 text-sm mb-3">
                          Copy your new API key now. For security, it won&apos;t be shown again in full.
                        </p>
                        <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-emerald-200">
                          <code className="flex-1 text-emerald-700 text-sm break-all font-mono">{newKey}</code>
                          <button
                            onClick={() => copyToClipboard(newKey, 'new')}
                            className="p-2 hover:bg-emerald-50 rounded flex-shrink-0"
                          >
                            {copied === 'new' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setNewKey(null)}
                      className="mt-3 text-sm text-emerald-600 hover:text-emerald-700"
                    >
                      Dismiss
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Credentials Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Partner ID Card */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Partner ID</p>
                      <p className="text-xs text-blue-500">Required in X-Partner-Id header</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-200">
                    <code className="flex-1 text-lg font-mono font-bold text-slate-800" data-testid="partner-id-display">
                      {displayId}
                    </code>
                    <button
                      onClick={copyPartnerId}
                      className="p-2 hover:bg-blue-50 rounded transition-colors"
                      title="Copy Partner ID"
                      data-testid="copy-partner-id-btn"
                    >
                      {copiedPartnerId ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-500" />
                      )}
                    </button>
                  </div>
                </div>

                {/* API Key Card */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Key className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-purple-700 font-medium">API Key</p>
                        <p className="text-xs text-purple-500">Required in X-API-Key header</p>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      apiKeyData?.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {apiKeyData?.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                    <code className="flex-1 text-sm font-mono text-slate-700 truncate" data-testid="api-key-display">
                      {showKey ? apiKeyData?.api_key : maskApiKey(apiKeyData?.api_key)}
                    </code>
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className="p-2 hover:bg-purple-50 rounded transition-colors"
                      title={showKey ? 'Hide Key' : 'Show Key'}
                    >
                      {showKey ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-slate-500" />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(apiKeyData?.api_key, 'key')}
                      className="p-2 hover:bg-purple-50 rounded transition-colors"
                      title="Copy API Key"
                    >
                      {copied === 'key' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Regenerate Button */}
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setShowConfirm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="regenerate-key-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate Key
                </button>
                
                <a
                  href="https://docs.nanokart.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  Full Documentation <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Security Note - Minimal */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Keep your API key secure. Never expose it in client-side code or public repositories.
                </p>
              </div>
            </div>
          )}

          {/* ========== API Docs Tab ========== */}
          {heroTab === 'api-docs' && (
            <div className="space-y-4">
              {/* Docs Sub-Tabs */}
              <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit flex-wrap">
                <button
                  onClick={() => setDocsTab('tryon')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    docsTab === 'tryon'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="docs-tab-tryon"
                >
                  TryOn API
                </button>
                <button
                  onClick={() => setDocsTab('moments')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    docsTab === 'moments'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="docs-tab-moments"
                >
                  Moments API
                </button>
                <button
                  onClick={() => setDocsTab('size')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    docsTab === 'size'
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="docs-tab-size"
                >
                  Size API
                </button>
              </div>

              {/* TryOn API Docs */}
              {docsTab === 'tryon' && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium">POST /api/v1/tryon/external</p>
                      <p className="text-blue-600">Generate virtual try-on images by combining human and garment photos.</p>
                    </div>
                  </div>
                  
                  <CodeBlock title="cURL Request" code={tryonExample} />
                  <ResponsePreview title="Example Success Response" response={tryonResponse} />

                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <h4 className="font-medium text-slate-700 mb-2">Request Parameters:</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li><code className="bg-slate-200 px-1 rounded">human_image_url</code> (required) - URL of the human/model image</li>
                      <li><code className="bg-slate-200 px-1 rounded">garment_image_url</code> (required) - URL of the garment image</li>
                      <li><code className="bg-slate-200 px-1 rounded">sku_id</code> (optional) - SKU ID for tracking</li>
                      <li><code className="bg-slate-200 px-1 rounded">customer_id</code> (optional) - Customer ID for analytics</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Moments API Docs */}
              {docsTab === 'moments' && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-purple-700">
                      <p className="font-medium">POST /api/v1/moments/external</p>
                      <p className="text-purple-600">Transform try-on results into lifestyle scenes using AI.</p>
                    </div>
                  </div>
                  
                  <CodeBlock title="cURL Request" code={momentsExample} />
                  <ResponsePreview title="Example Success Response" response={momentsResponse} />

                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <h4 className="font-medium text-slate-700 mb-2">Request Parameters:</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li><code className="bg-slate-200 px-1 rounded">tryon_key</code> (required) - tryon_key from a previous TryOn result</li>
                      <li><code className="bg-slate-200 px-1 rounded">moment_type</code> (required) - One of the lifestyle moment types</li>
                      <li><code className="bg-slate-200 px-1 rounded">custom_prompt</code> (optional) - Additional scene description</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-medium text-purple-800 mb-2">Available Moment Types:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {momentTypes.map((moment) => (
                        <div key={moment.value} className="flex items-start gap-2 p-2 bg-white rounded border border-purple-100">
                          <code className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-mono">{moment.value}</code>
                          <span className="text-xs text-slate-600">{moment.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Size Recommendation API Docs */}
              {docsTab === 'size' && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-700">
                      <p className="font-medium">POST /api/v1/size/recommend</p>
                      <p className="text-emerald-600">Get AI-powered size recommendation using a customer's body from their try-on image and your product size chart.</p>
                    </div>
                  </div>

                  {/* Flow steps */}
                  <div className="bg-blue-50 rounded-lg p-4 text-sm border border-blue-100">
                    <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
                    <ol className="space-y-1.5 text-blue-700">
                      <li className="flex items-start gap-2">
                        <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">1</span>
                        Run a TryOn and save the <code className="bg-blue-100 px-1 rounded">tryon_key</code>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">2</span>
                        Pass <code className="bg-blue-100 px-1 rounded">tryon_key</code> as <code className="bg-blue-100 px-1 rounded">image_s3_key</code> + customer height + size chart
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">3</span>
                        Receive recommended size, confidence %, and natural language fit notes
                      </li>
                    </ol>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <h4 className="font-medium text-slate-700 mb-2">Request Parameters:</h4>
                    <ul className="space-y-1.5 text-slate-600">
                      <li><code className="bg-slate-200 px-1 rounded">image_s3_key</code> <span className="text-red-500">(required)</span>  -  The <code className="bg-slate-200 px-1 rounded">tryon_key</code> from TryOn response</li>
                      <li><code className="bg-slate-200 px-1 rounded">height_cm</code> <span className="text-red-500">(required)</span>  -  Customer height in cm (50–250)</li>
                      <li><code className="bg-slate-200 px-1 rounded">product_id</code> <span className="text-amber-600">(conditional)</span>  -  Auto-fetches size chart from your inventory</li>
                      <li><code className="bg-slate-200 px-1 rounded">size_chart_cm</code> <span className="text-amber-600">(conditional)</span>  -  Raw size chart JSON if not using product_id</li>
                      <li><code className="bg-slate-200 px-1 rounded">sku_id</code>, <code className="bg-slate-200 px-1 rounded">customer_id</code>  -  Optional, for analytics</li>
                    </ul>
                    <p className="text-xs text-slate-500 mt-2">Either <code className="bg-slate-200 px-1 rounded">product_id</code> or <code className="bg-slate-200 px-1 rounded">size_chart_cm</code> is required.</p>
                  </div>

                  <CodeBlock title="cURL  -  Using product_id (recommended)" code={sizeRecommendExample} />
                  <ResponsePreview title="Example Success Response" response={sizeRecommendResponse} />

                  <CodeBlock title="cURL  -  Using size_chart_cm directly" code={sizeRecommendWithChartExample} />

                  {/* Size chart format */}
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <h4 className="font-medium text-emerald-800 mb-2">Size Chart Format (size_chart_cm)</h4>
                    <p className="text-xs text-emerald-700 mb-2">A JSON object keyed by size label. Supported measurement keys:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {['chest_cm', 'waist_cm', 'hip_cm', 'shoulder_cm', 'length_cm', 'inseam_cm'].map(k => (
                        <code key={k} className="text-[11px] bg-white border border-emerald-200 text-emerald-700 px-2 py-1 rounded">{k}</code>
                      ))}
                    </div>
                  </div>

                  {/* Response fields */}
                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <h4 className="font-medium text-slate-700 mb-2">Response Fields:</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li><code className="bg-slate-200 px-1 rounded">recommended_size</code>  -  e.g. "M", "L", "42", "S/M"</li>
                      <li><code className="bg-slate-200 px-1 rounded">confidence_percent</code>  -  0–100, AI fit confidence score</li>
                      <li><code className="bg-slate-200 px-1 rounded">fit_notes</code>  -  Human-readable fit advice</li>
                      <li><code className="bg-slate-200 px-1 rounded">photo_sufficient</code>  -  false if body was partially visible</li>
                      <li><code className="bg-slate-200 px-1 rounded">insufficiency_message</code>  -  Shown when confidence is low</li>
                    </ul>
                  </div>

                  {/* Fetch size chart endpoint */}
                  <div className="border-t border-slate-200 pt-4">
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100 mb-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-700">
                        <p className="font-medium">GET /api/v1/inventory/&#123;product_id&#125;/size-chart</p>
                        <p className="text-blue-600">Fetch the size chart stored for a product in your Nanokart inventory.</p>
                      </div>
                    </div>
                    <CodeBlock title="Fetch Size Chart" code={sizeChartFetchExample} />
                    <ResponsePreview title="Example Size Chart Response" response={sizeChartResponse} />
                  </div>

                  {/* ── New: Size Recommendation from Photo ── */}
                  <div className="border-t border-slate-200 pt-4 space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-violet-50 rounded-lg border border-violet-100">
                      <Info className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-violet-700">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="font-medium">POST /api/v1/size/recommend-from-photo</p>
                          <span className="px-1.5 py-0.5 bg-violet-200 text-violet-800 text-[10px] font-semibold rounded">multipart/form-data</span>
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">No TryOn required</span>
                        </div>
                        <p className="text-violet-600">Upload a customer photo directly  -  no TryOn needed. Images are auto-compressed to ≤ 1 MB server-side.</p>
                      </div>
                    </div>

                    <div className="bg-violet-50 rounded-lg p-4 text-sm border border-violet-100">
                      <h4 className="font-medium text-violet-800 mb-2">How it works:</h4>
                      <ol className="space-y-1.5 text-violet-700">
                        <li className="flex items-start gap-2">
                          <span className="w-4 h-4 rounded-full bg-violet-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">1</span>
                          Send a <code className="bg-violet-100 px-1 rounded">multipart/form-data</code> POST with the photo file and customer height
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-4 h-4 rounded-full bg-violet-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">2</span>
                          Pass <code className="bg-violet-100 px-1 rounded">product_id</code> to auto-fetch the size chart, or provide <code className="bg-violet-100 px-1 rounded">size_chart_cm</code> as a JSON string
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-4 h-4 rounded-full bg-violet-600 text-white flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">3</span>
                          Receive the recommended size, confidence %, and fit notes  -  same response as <code className="bg-violet-100 px-1 rounded">/size/recommend</code>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4 text-sm">
                      <h4 className="font-medium text-slate-700 mb-2">Request Fields (multipart/form-data):</h4>
                      <ul className="space-y-1.5 text-slate-600">
                        <li><code className="bg-slate-200 px-1 rounded">human_image</code> <span className="text-red-500">(required)</span>  -  Customer photo file (JPEG, PNG, WEBP, HEIC)</li>
                        <li><code className="bg-slate-200 px-1 rounded">height_cm</code> <span className="text-red-500">(required)</span>  -  Customer height in cm (100–250)</li>
                        <li><code className="bg-slate-200 px-1 rounded">product_id</code> <span className="text-amber-600">(conditional)</span>  -  Auto-fetches size chart and gender</li>
                        <li><code className="bg-slate-200 px-1 rounded">size_chart_cm</code> <span className="text-amber-600">(conditional)</span>  -  Size chart as a JSON string if not using product_id</li>
                        <li><code className="bg-slate-200 px-1 rounded">gender</code>  -  <code className="bg-slate-200 px-1 rounded">male</code> / <code className="bg-slate-200 px-1 rounded">female</code> / <code className="bg-slate-200 px-1 rounded">unisex</code>. Auto-detected from product.</li>
                        <li><code className="bg-slate-200 px-1 rounded">sku_id</code>, <code className="bg-slate-200 px-1 rounded">customer_id</code>  -  Optional, for analytics</li>
                      </ul>
                      <p className="text-xs text-slate-500 mt-2">Do <strong>not</strong> set <code className="bg-slate-200 px-1 rounded">Content-Type</code> manually  -  your HTTP client sets the multipart boundary automatically.</p>
                    </div>

                    <CodeBlock title="cURL  -  Using product_id (recommended)" code={sizeRecommendFromPhotoExample} />
                    <ResponsePreview title="Example Success Response" response={sizeRecommendFromPhotoResponse} />
                    <CodeBlock title="cURL  -  Inline size_chart_cm" code={sizeRecommendFromPhotoWithChartExample} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ==================== SECTION B: OPERATIONS ==================== */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Operations Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Activity className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Integration Operations</h2>
              <p className="text-slate-500 text-sm">Monitor your API usage and activity</p>
            </div>
          </div>
        </div>

        {/* Operations Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setOpsTab('usage')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              opsTab === 'usage'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="ops-tab-usage"
          >
            <BarChart2 className="w-4 h-4 inline mr-2" />
            Usage Overview
          </button>
          <button
            onClick={() => setOpsTab('logs')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              opsTab === 'logs'
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="ops-tab-logs"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Request Logs
          </button>
        </div>

        {/* Operations Tab Content */}
        <div className="p-5 sm:p-6">
          
          {/* ========== Usage Overview Tab ========== */}
          {opsTab === 'usage' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-700">This Month</h3>
                {apiKeyData?.last_used && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Last used: {format(parseISO(apiKeyData.last_used), 'MMM d, h:mm a')}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatsCard
                  icon={Zap}
                  title="API Calls"
                  value={formatCredits(apiKeyData?.usage?.total_calls || 0)}
                  subtitle="Total requests"
                />
                <StatsCard
                  icon={Coins}
                  title="Credits Used"
                  value={formatCredits(apiKeyData?.usage?.credits_consumed || 0)}
                  subtitle="Via API"
                />
                <StatsCard
                  icon={BarChart2}
                  title="TryOn Calls"
                  value={formatCredits(apiKeyData?.usage?.tryon_calls || 0)}
                  subtitle={`${formatCredits(apiKeyData?.usage?.tryon_credits || 0)} credits`}
                />
                <StatsCard
                  icon={BarChart2}
                  title="Moments Calls"
                  value={formatCredits(apiKeyData?.usage?.moments_calls || 0)}
                  subtitle={`${formatCredits(apiKeyData?.usage?.moments_credits || 0)} credits`}
                />
              </div>

              {/* Quick Reference */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <FileCode className="w-4 h-4 text-slate-600" />
                  Quick Reference
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-slate-500 text-xs mb-1">Base URL</p>
                    <code className="text-slate-700 font-mono text-xs">https://api.nanokart.ai/api/v1</code>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-slate-500 text-xs mb-1">Required Headers</p>
                    <code className="text-slate-700 font-mono text-xs">X-API-Key, X-Partner-Id</code>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== Request Logs Tab ========== */}
          {opsTab === 'logs' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-medium text-slate-700 mb-2">Request logs will appear here once enabled</h3>
              <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                View detailed API request history including timestamps, status codes, and response times.
              </p>
              <Link
                to="/partners/audit-logs"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                data-testid="view-audit-logs-btn"
              >
                <FileText className="w-4 h-4" />
                View Audit Logs
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ==================== Regenerate Confirmation Modal ==================== */}
      <AnimatePresence>
        {showConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Regenerate API Key?</h2>
                  <p className="text-sm text-slate-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-3 mb-4 text-sm text-amber-800">
                <p>⚠️ Your current API key will be <strong>immediately deactivated</strong>. Any applications using it will stop working.</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-regenerate-btn"
                >
                  {regenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Regenerate
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Integrations;
